import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Bell, X, Gauge, Zap, Check, Volume2, MapPin, Play, BatteryLow, Power, CreditCard, UserRound } from 'lucide-react';
import { format } from 'date-fns';
import { formatDT, resolveTimezone } from '../../utils/timezone';
import { getTimezoneLabel } from '../../utils/timezoneList';
import EventDetailModal from './EventDetailModal';
import DriverConfirmDialog from './DriverConfirmDialog';
import DriverRespondDialog from './DriverRespondDialog';
import { formatRecordDeviceLabel } from '../../utils/deviceLabel';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { scanAlertQuickAction, personOpenAction } from '../../utils/scanAlertLink';
import { shouldSuppressSound } from '../../utils/quietHours';

// Same alert tones as Road Speed Alert + Event Rules. Kept in `constants/alertTones.js`
// so Settings + Event Rules share the same list. The synth cases below MUST match every id.
const TONES = {
  default: { freq: 800, dur: 200 },
  alarm: { freq: 1200, dur: 400 },
  chime: { freq: 600, dur: 150 },
  urgent_siren: { freq: 900, dur: 600 },
  triple_beep: { freq: 1000, dur: 150 },
  continuous: { freq: 1100, dur: 300 },
  low_warning: { freq: 350, dur: 500 },
};

export const playAlertTone = (toneId) => {
  const t = TONES[toneId];
  if (!t) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    if (toneId === 'urgent_siren') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sawtooth'; gain.gain.value = 0.3;
      osc.frequency.setValueAtTime(900, now);
      osc.frequency.linearRampToValueAtTime(1400, now + 0.15);
      osc.frequency.linearRampToValueAtTime(900, now + 0.3);
      osc.frequency.linearRampToValueAtTime(1400, now + 0.45);
      osc.frequency.linearRampToValueAtTime(900, now + 0.6);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
      osc.start(now); osc.stop(now + 0.65);
    } else if (toneId === 'triple_beep') {
      for (let i = 0; i < 3; i++) {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = t.freq; o.type = 'sine'; g.gain.value = 0.3;
        g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.18 + 0.12);
        o.start(now + i * 0.18); o.stop(now + i * 0.18 + 0.15);
      }
    } else if (toneId === 'continuous') {
      for (let i = 0; i < 3; i++) {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = t.freq; o.type = 'square'; g.gain.value = 0.2;
        g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.4 + 0.25);
        o.start(now + i * 0.4); o.stop(now + i * 0.4 + 0.3);
      }
    } else if (toneId === 'low_warning') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = t.freq; osc.type = 'triangle'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc.start(now); osc.stop(now + 0.55);
    } else if (toneId === 'alarm') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = t.freq; osc.type = 'sine'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + t.dur / 1000);
      osc.start(now); osc.stop(now + t.dur / 1000 + 0.05);
      const osc2 = ctx.createOscillator(); const gain2 = ctx.createGain();
      osc2.connect(gain2); gain2.connect(ctx.destination);
      osc2.frequency.value = t.freq; osc2.type = 'sine'; gain2.gain.value = 0.3;
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.25 + t.dur / 1000);
      osc2.start(now + 0.25); osc2.stop(now + 0.25 + t.dur / 1000 + 0.05);
    } else {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = t.freq; osc.type = 'sine'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + t.dur / 1000);
      osc.start(now); osc.stop(now + t.dur / 1000 + 0.05);
    }
    setTimeout(() => ctx.close(), 2000);
  } catch { /* Audio not available */ }
};

const driverTripPhrase = (notif, timezone) => {
  const start = notif.trip_started_at || notif.ballot_sent_at || notif.triggered_at || notif.created_at;
  const end = notif.trip_ended_at;
  if (start && end) {
    return `${formatDT(start, { timezone, seconds: true })}–${formatDT(end, { timezone, seconds: true })}`;
  }
  if (start) return `Started ${formatDT(start, { timezone, seconds: true })}`;
  return '';
};

const notificationDisplayMessage = (notif, timezone) => {
  if (notif.notification_type !== 'driver_confirmation') return notif.message;
  const vehicle = notif.device_name || notif.device_label || 'vehicle';
  const phrase = driverTripPhrase(notif, timezone);
  if (notif.audience === 'manager') {
    if (notif.manager_correction_available) {
      return `Auto-assigned ${notif.auto_assigned_person_name || 'last remaining Driver'} — review assignment: ${vehicle}${phrase ? ` · ${phrase}` : ''}`;
    }
    if (notif.manager_review_required) {
      if (notif.resolution_reason === 'ignition_off_before_driver_decision') {
        return `Trip ended without a Driver — review driver: ${vehicle}${phrase ? ` · ${phrase}` : ''}`;
      }
      if (notif.resolution_reason === 'moving_max_no_qualified_candidates') {
        return `No qualified Driver — review driver: ${vehicle}${phrase ? ` · ${phrase}` : ''}`;
      }
      return `All candidates declined — review driver: ${vehicle}${phrase ? ` · ${phrase}` : ''}`;
    }
    const prefix = notif.severity === 'warning' ? 'Assign driver' : 'Confirm driver';
    return `${prefix}: ${vehicle}${phrase ? ` · ${phrase}` : ''}`;
  }
  return `Driving ${vehicle}?${phrase ? ` ${phrase}.` : ''}`;
};

export const EventNotificationBell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user, onDeviceUpdate } = useAuth();
  const { isSubpageEnabled } = useFeatureFlags();
  const canViewEventVideos = isSubpageEnabled('media_event_videos');
  // Person (chat_contact) logins answer for THEMSELVES (accept/decline); everyone else
  // gets the manager "who is driving?" resolve dialog.
  const isPersonLogin = user?.role === 'chat_contact' && !!user?.person_ref_id;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissVisible, setDismissVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [driverCandidateId, setDriverCandidateId] = useState(null);
  const [driverConfirmOpen, setDriverConfirmOpen] = useState(false);
  const [driverRespondOpen, setDriverRespondOpen] = useState(false);
  const bellRef = useRef(null);
  const alertIntervalRef = useRef(null);
  // Set of notification IDs seen on the previous poll, used to detect NEW driver-confirmation
  // notifications and play the user's chosen tone even when the live WS branch missed the
  // event (mobile network flap, backgrounded tab, etc.).
  const seenNotificationIdsRef = useRef(null);
  // Candidate IDs whose tone we've already emitted this session. Prevents the WS branch AND
  // the 30 s polling-delta safety net from BOTH firing the same tone within the same window.
  const playedCandidateIdsRef = useRef(new Set());
  const toastedCandidateIdsRef = useRef(new Set());
  // Audit-alert toast dedupe survives page refreshes AND full browser restarts
  // (localStorage, not sessionStorage) so a recent alert already toasted before the
  // window closed doesn't re-toast next to the new one on the next sign-in.
  const toastedAuditAlertIdsRef = useRef((() => {
    try { return new Set(JSON.parse(localStorage.getItem('toasted_audit_alert_ids') || '[]')); }
    catch { return new Set(); }
  })());
  const rememberToastedAuditAlert = (id) => {
    toastedAuditAlertIdsRef.current.add(id);
    try {
      localStorage.setItem('toasted_audit_alert_ids', JSON.stringify([...toastedAuditAlertIdsRef.current].slice(-100)));
    } catch { /* storage full/unavailable — in-memory dedupe still applies */ }
  };
  // User's own driver-confirmation sound preference, refreshed on mount + window focus so
  // the bell honours changes made in Settings without reloading the page.
  const driverConfSoundPrefRef = useRef({ enabled: true, file: 'default', duration: 'once' });

  // Stop any repeating sound
  const dismissSound = useCallback(() => {
    if (alertIntervalRef.current) {
      clearInterval(alertIntervalRef.current);
      alertIntervalRef.current = null;
    }
    setDismissVisible(false);
  }, []);

  // Play the given tone with the given duration (once / Ns / until_dismissed) — shared by
  // the real-time WS branch AND the polling-delta safety net so both behave identically.
  const playAlertWithDuration = useCallback((soundFile, soundDuration, severity = null) => {
    if (!soundFile || soundFile === 'none') return;
    if (shouldSuppressSound(severity)) return;
    if (alertIntervalRef.current) { clearInterval(alertIntervalRef.current); alertIntervalRef.current = null; }
    playAlertTone(soundFile);
    if (soundDuration === 'until_dismissed') {
      alertIntervalRef.current = setInterval(() => playAlertTone(soundFile), 1500);
      setDismissVisible(true);
    } else if (soundDuration && soundDuration !== 'once') {
      const secs = soundDuration === '3s' ? 3 : soundDuration === '5s' ? 5 : 10;
      alertIntervalRef.current = setInterval(() => playAlertTone(soundFile), 1500);
      setDismissVisible(true);
      setTimeout(() => {
        if (alertIntervalRef.current) { clearInterval(alertIntervalRef.current); alertIntervalRef.current = null; }
        setDismissVisible(false);
      }, secs * 1000);
    }
  }, []);

  // Refresh the driver-confirmation sound preference from Settings → Notifications.
  // Called at bell mount + whenever the window regains focus.
  const refreshDriverConfSoundPref = useCallback(async () => {
    try {
      const res = await api.get('/settings/notifications');
      const d = res.data || {};
      driverConfSoundPrefRef.current = {
        enabled: d.driver_confirmation_sound_enabled !== false,
        file: d.driver_confirmation_sound_file || 'default',
        duration: d.driver_confirmation_sound_duration || 'once',
        popup: d.driver_confirmation_popup_enabled !== false,
      };
    } catch { /* keep previous pref on failure */ }
  }, [api]);

  // Fetch notifications on mount and periodically
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/event-notifications?limit=30');
      const list = res.data.notifications || [];
      // Polling-delta safety net: detect NEW driver-confirmation notifications since the
      // previous poll and play the user's chosen tone. First poll (ref === null) seeds the
      // set without playing so an unread backlog on page load doesn't blast a tone.
      const prev = seenNotificationIdsRef.current;
      const currentIds = new Set(list.map(n => n.id).filter(Boolean));
      // Toast safety net for security/audit alerts: new since last poll, or (on the very
      // first poll, e.g. a tab opened right as an impersonation starts) unread and fresh.
      const newAuditAlerts = list.filter(n =>
        n?.id && n.notification_type === 'audit_alert' && n.read !== true &&
        !toastedAuditAlertIdsRef.current.has(n.id) &&
        (prev !== null
          ? !prev.has(n.id)
          : (Date.now() - new Date(n.created_at || 0).getTime()) < 120000)
      );
      newAuditAlerts.forEach(n => {
        rememberToastedAuditAlert(n.id);
        toast.warning(n.event_rule_name || 'Security alert', {
          id: `audit-alert-${n.id}`,
          description: n.message || '',
          duration: 12000,
          closeButton: true,
          ...(n.link_url ? {
            action: {
              label: 'View',
              onClick: () => navigate(n.link_url),
            },
          } : {}),
        });
      });
      if (prev !== null) {
        const pref = driverConfSoundPrefRef.current;
        const newDriverConf = list.filter(
          n => n?.id && !prev.has(n.id) && n.notification_type === 'driver_confirmation',
        );
        const unplayed = newDriverConf.filter(
          n => !n.candidate_id || !playedCandidateIdsRef.current.has(n.candidate_id),
        );
        if (pref.enabled && unplayed.length > 0) {
          playAlertWithDuration(pref.file, pref.duration);
        }
        unplayed.forEach(n => {
          if (n.candidate_id) playedCandidateIdsRef.current.add(n.candidate_id);
        });
        // System Message safety net: WS normally shows the toast instantly; if the socket
        // missed the ballot, pop it here (same per-candidate dedupe, so never doubles).
        if (pref.popup !== false) {
          newDriverConf
            .filter(n => n.candidate_id && n.resolved !== true && !toastedCandidateIdsRef.current.has(n.candidate_id))
            .forEach(n => {
              toastedCandidateIdsRef.current.add(n.candidate_id);
              const tz = n.device_timezone || resolveTimezone();
              const when = n.triggered_at ? formatDT(n.triggered_at, { timezone: tz, seconds: true }) : '';
              const tzLabel = getTimezoneLabel(tz) || tz;
              toast.info('Driver confirmation', {
                id: `driver-conf-${n.candidate_id}`,
                description: React.createElement('div', { className: 'flex flex-col gap-0.5 text-xs' },
                  React.createElement('span', null, n.message || 'Confirm driver'),
                  !isPersonLogin && when && React.createElement('span', { className: 'text-muted-foreground' }, `${when} — ${tzLabel}`)
                ),
                // Managers: sticky until acted on (Confirm / close / resolved elsewhere).
                duration: isPersonLogin ? 12000 : Infinity,
                closeButton: !isPersonLogin,
                action: {
                  label: 'Confirm',
                  onClick: () => {
                    setDriverCandidateId(n.candidate_id);
                    if (isPersonLogin) setDriverRespondOpen(true); else setDriverConfirmOpen(true);
                  },
                },
              });
            });
        }
      }
      // Sticky-toast sweep: a ballot resolved elsewhere (another manager, the driver, or
      // auto-assignment) marks its notifications resolved — dismiss its lingering toast.
      list
        .filter(n => n.notification_type === 'driver_confirmation' && n.resolved === true && n.candidate_id)
        .forEach(n => toast.dismiss(`driver-conf-${n.candidate_id}`));
      seenNotificationIdsRef.current = currentIds;
      setNotifications(list);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* ignore */ }
  }, [api, playAlertWithDuration, isPersonLogin, navigate]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Refresh the driver-confirmation sound preference on mount + on window focus so a change
  // made in Settings is picked up without a full reload.
  useEffect(() => {
    refreshDriverConfSoundPref();
    const onFocus = () => refreshDriverConfSoundPref();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshDriverConfSoundPref]);

  // Phase 3C deep link — a web-push tap lands on ?driver_confirm=<candidate_id>: open the
  // right dialog (person accept/decline vs manager resolve) and strip the param. If the
  // ballot was already answered/expired, show a small notice instead of the dialog.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cid = params.get('driver_confirm');
    if (!cid) return;
    params.delete('driver_confirm');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    api.get(`/fleet/driver-candidates/${cid}`)
      .then((res) => {
        const c = res.data || {};
        const stillOpen = c.status === 'pending' || (c.status === 'no_driver' && c.manager_review_required === true);
        if (stillOpen || isPersonLogin) {
          setDriverCandidateId(cid);
          if (isPersonLogin) setDriverRespondOpen(true); else setDriverConfirmOpen(true);
        } else {
          toast.info('This driver confirmation has already been resolved');
        }
      })
      .catch(() => {
        toast.info('This driver confirmation has already been resolved');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, isPersonLogin, navigate]);

  // Listen for real-time WebSocket event notifications
  useEffect(() => {
    const unsubscribe = onDeviceUpdate?.((deviceId, data) => {
      // Driver-confirmation bell tone. Backend broadcasts this the instant the ballot's
      // "Are you driving X?" notification is created. Sound is looked up from the recipient's
      // Settings → Notifications preference by the backend and attached to the payload.
      if (data.type === 'driver_confirmation_alert') {
        const cid = data.candidate_id;
        const cfg = data.notifications || {};
        // Pop-up with a Confirm action (gated by the user's Settings toggle via the backend).
        if (cfg.system_message && cid && !toastedCandidateIdsRef.current.has(cid)) {
          toastedCandidateIdsRef.current.add(cid);
          const tz = resolveTimezone();
          const when = formatDT(new Date().toISOString(), { timezone: tz, seconds: true });
          const tzLabel = getTimezoneLabel(tz) || tz;
          toast.info('Driver confirmation', {
            id: `driver-conf-${cid}`,
            description: React.createElement('div', { className: 'flex flex-col gap-0.5 text-xs' },
              React.createElement('span', null,
                isPersonLogin ? `Driving ${data.device_name || 'a vehicle'}?` : `Confirm driver: ${data.device_name || 'vehicle'}`),
              !isPersonLogin && React.createElement('span', { className: 'text-muted-foreground' }, `${when} — ${tzLabel}`)
            ),
            // Managers: sticky until acted on (Confirm / close / resolved elsewhere).
            duration: isPersonLogin ? 12000 : Infinity,
            closeButton: !isPersonLogin,
            action: {
              label: 'Confirm',
              onClick: () => {
                setDriverCandidateId(cid);
                if (isPersonLogin) setDriverRespondOpen(true); else setDriverConfirmOpen(true);
              },
            },
          });
        }
        if (cid && playedCandidateIdsRef.current.has(cid)) {
          // Polling delta already emitted the tone for this candidate; skip WS duplicate.
          fetchNotifications();
          return;
        }
        if (cfg.sound_alert && cfg.sound_file && cfg.sound_file !== 'none') {
          playAlertWithDuration(cfg.sound_file, cfg.sound_duration || 'once');
          if (cid) playedCandidateIdsRef.current.add(cid);
        }
        fetchNotifications();
        return;
      }
      // Security/audit alerts — instant WS path; the 30s bell poll stays as the
      // safety net and dedupes on the same notification id.
      if (data.type === 'audit_alert') {
        if (data.id && !toastedAuditAlertIdsRef.current.has(data.id)) {
          rememberToastedAuditAlert(data.id);
          toast.warning(data.title || 'Security alert', {
            id: `audit-alert-${data.id}`,
            description: data.message || '',
            duration: 12000,
            closeButton: true,
            ...(data.link_url ? {
              action: {
                label: 'View',
                onClick: () => navigate(data.link_url),
              },
            } : {}),
          });
        }
        fetchNotifications();
        return;
      }
      if (data.type === 'event_occurrence' || (data.type === 'alert' && data.event_type)) {
        const notifConfig = data.notifications || {};

        // Play sound alert (global — works on any page) with duration support
        if (notifConfig.sound_alert && notifConfig.sound_file && notifConfig.sound_file !== 'none') {
          playAlertWithDuration(notifConfig.sound_file, notifConfig.sound_duration || 'once', data.severity || null);
        }

        // Show system message toast
        if (notifConfig.system_message !== false) {
          const severity = data.severity || 'warning';
          const toastFn = severity === 'critical' ? toast.error : severity === 'warning' ? toast.warning : toast.info;

          const eventTz = data.device_timezone || resolveTimezone();
          const eventTime = data.triggered_at ? formatDT(data.triggered_at, { timezone: eventTz, seconds: true }) : '';
          const tzLabel = getTimezoneLabel(eventTz) || eventTz;
          const hasLocation = data.latitude != null && data.longitude != null;
          const mapsUrl = hasLocation ? `https://maps.google.com/?q=${data.latitude},${data.longitude}` : null;
          const scanAction = scanAlertQuickAction(data.event_type, data.metadata?.credential)
            || personOpenAction(data.event_type, data.metadata?.person_type, data.metadata?.person_id);

          const descriptionEl = React.createElement('div', { className: 'flex flex-col gap-0.5 text-xs' },
            React.createElement('span', { className: 'font-semibold' }, formatRecordDeviceLabel(data)),
            data.message && React.createElement('span', null, data.message),
            eventTime && React.createElement('span', { className: 'text-muted-foreground' }, `${eventTime} — ${tzLabel}`),
            mapsUrl && React.createElement('a', { href: mapsUrl, target: '_blank', rel: 'noopener noreferrer', className: 'flex items-center gap-1 text-blue-500 hover:underline', onClick: (e) => e.stopPropagation() },
              React.createElement(MapPin, { className: 'w-3 h-3 flex-shrink-0' }),
              `${Number(data.latitude).toFixed(5)}, ${Number(data.longitude).toFixed(5)}`
            ),
            scanAction && React.createElement('button', {
              type: 'button',
              className: 'flex items-center gap-1 text-blue-500 hover:underline font-semibold text-left',
              'data-testid': 'toast-scan-quick-action',
              onClick: (e) => { e.stopPropagation(); navigate(scanAction.url); },
            },
              React.createElement(scanAction.kind === 'person' ? UserRound : CreditCard, { className: 'w-3 h-3 flex-shrink-0' }),
              scanAction.label
            )
          );

          const titleEl = React.createElement('span', { className: 'font-extrabold text-sm' }, data.title || data.event_rule_name);

          if (notifConfig.auto_hide) {
            toastFn(titleEl, {
              description: descriptionEl,
              duration: 5000,
            });
          } else {
            toastFn(titleEl, {
              description: descriptionEl,
              duration: Infinity,
              action: {
                label: 'Dismiss',
                onClick: () => dismissSound(),
              },
            });
          }
        }

        // Update notification count
        setUnreadCount(prev => prev + 1);
        fetchNotifications();
      }
    });

    return () => unsubscribe?.();
  }, [onDeviceUpdate, fetchNotifications, dismissSound, playAlertWithDuration]);

  // Cleanup repeating sound on unmount
  useEffect(() => {
    return () => { if (alertIntervalRef.current) clearInterval(alertIntervalRef.current); };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const markAllRead = async () => {
    try {
      await api.put('/event-notifications/mark-read', { all: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  // Video-event clip thumbnails: lazily matched when the dropdown is open.
  const [videoThumbs, setVideoThumbs] = useState({});
  const videoThumbFetchingRef = useRef(new Set());

  useEffect(() => {
    if (!isOpen || !canViewEventVideos) return;
    notifications
      .filter(n => n.event_type === 'video_event' && n.device_id && videoThumbs[n.id] === undefined && !videoThumbFetchingRef.current.has(n.id))
      .slice(0, 15)
      .forEach(async (n) => {
        videoThumbFetchingRef.current.add(n.id);
        try {
          const res = await api.get('/media/event-videos-match', {
            params: { device_id: n.device_id, triggered_at: n.triggered_at || n.created_at, include_thumbnail: true },
          });
          setVideoThumbs(prev => ({ ...prev, [n.id]: res.data?.video_id ? res.data : null }));
        } catch {
          setVideoThumbs(prev => ({ ...prev, [n.id]: null }));
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, notifications, canViewEventVideos]);

  const markNotifRead = (notif) => {
    if (notif.read) return;
    api.put('/event-notifications/mark-read', { ids: [notif.id] }).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const openExactClip = (notif, match) => {
    const tz = notif.device_timezone || resolveTimezone();
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(new Date(notif.triggered_at || notif.created_at));
    const get = (t) => parts.find(p => p.type === t)?.value || '00';
    const hh = get('hour') === '24' ? '00' : get('hour');
    const ids = (match.matches || []).map(m => m.video_id).filter(Boolean);
    const videoIds = ids.length > 0 ? ids.join(',') : match.video_id;
    markNotifRead(notif);
    setIsOpen(false);
    navigate(`/media?tab=events&device_id=${notif.device_id}&date=${get('year')}-${get('month')}-${get('day')}&time=${hh}:${get('minute')}:${get('second')}&video_id=${videoIds}`);
  };

  return (
    <div ref={bellRef} className="relative flex items-center gap-1" data-testid="event-notification-bell">
      {/* Dismiss sound button — appears when sound is repeating */}
      {dismissVisible && (
        <Button
          variant="destructive"
          size="sm"
          className="h-8 text-xs gap-1 animate-pulse"
          onClick={dismissSound}
          data-testid="dismiss-sound-btn"
        >
          <Volume2 className="w-3.5 h-3.5" /> Dismiss
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon"
        className="relative h-9 w-9"
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) fetchNotifications(); }}
        data-testid="topbar-notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div
          className="fixed left-2 right-2 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-card border border-border rounded-lg shadow-xl z-[1200]"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Event Notifications</h3>
              {unreadCount > 0 && <Badge className="bg-red-500 text-white text-[10px]">{unreadCount}</Badge>}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead} data-testid="mark-all-read-btn">
                  <Check className="w-3 h-3 mr-1" /> Mark all read
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsOpen(false)}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Notifications list */}
          <div className="max-h-[60vh] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map(notif => {
                  const isSpeed = notif.event_type?.includes('overspeed');
                  const severityColor = notif.severity === 'critical' ? 'bg-red-500' : notif.severity === 'warning' ? 'bg-amber-500' : 'bg-slate-400';
                  const unreadRowClass = !notif.read
                    ? 'relative bg-sky-100 text-sky-950 shadow-[inset_4px_0_0_#0284c7] hover:bg-sky-200/90 dark:bg-cyan-950/80 dark:text-cyan-50 dark:shadow-[inset_4px_0_0_#22d3ee] dark:hover:bg-cyan-900/90'
                    : 'hover:bg-muted/50';
                  const notifTz = notif.device_timezone || resolveTimezone();
                  const notifTzLabel = getTimezoneLabel(notifTz) || notifTz;
                  const notifTriggeredAt = notif.triggered_at || notif.created_at;
                  const displayMessage = notificationDisplayMessage(notif, notifTz);
                  return (
                    <div
                      key={notif.id}
                      className={`px-4 py-2.5 transition-colors cursor-pointer ${unreadRowClass}`}
                      onClick={async () => {
                        // Phase 3B/3C — driver confirmation: person logins answer for
                        // themselves; managers pick who is driving.
                        if (notif.notification_type === 'driver_confirmation') {
                          setDriverCandidateId(notif.candidate_id);
                          if (isPersonLogin) setDriverRespondOpen(true); else setDriverConfirmOpen(true);
                          setIsOpen(false);
                          if (!notif.read) {
                            api.put('/event-notifications/mark-read', { ids: [notif.id] }).catch(() => {});
                            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                            setUnreadCount(prev => Math.max(0, prev - 1));
                          }
                          return;
                        }
                        // Security/audit alerts deep-link to their stored destination
                        // (Audit Log for admins, Settings for the affected user).
                        if (notif.notification_type === 'audit_alert') {
                          if (!notif.read) {
                            api.put('/event-notifications/mark-read', { ids: [notif.id] }).catch(() => {});
                            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                            setUnreadCount(prev => Math.max(0, prev - 1));
                          }
                          setIsOpen(false);
                          if (notif.link_url) navigate(notif.link_url);
                          return;
                        }
                        // Condition/health alerts (Low Beacon Battery, Device
                        // Disconnected) deep-link to the Analytics > Alerts page.
                        if (notif.event_type === 'beacon_low_battery' || notif.event_type === 'device_disconnected') {
                          navigate('/analytics?tab=alerts');
                          setIsOpen(false);
                          if (!notif.read) {
                            api.put('/event-notifications/mark-read', { ids: [notif.id] }).catch(() => {});
                            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                            setUnreadCount(prev => Math.max(0, prev - 1));
                          }
                          return;
                        }
                        let eventData = {
                          id: notif.occurrence_id || notif.id,
                          event_rule_name: notif.event_rule_name,
                          event_type: notif.event_type,
                          device_id: notif.device_id,
                          device_name: notif.device_name,
                          severity: notif.severity,
                          status: 'active',
                          triggered_at: notifTriggeredAt,
                          device_timezone: notif.device_timezone,
                          latitude: notif.latitude,
                          longitude: notif.longitude,
                          cleared_at: notif.cleared_at,
                        };
                        if (notif.occurrence_id) {
                          try {
                            const res = await api.get(`/event-occurrences/${notif.occurrence_id}`);
                            if (res.data) eventData = res.data;
                          } catch { /* use fallback */ }
                        }
                        setSelectedEvent(eventData);
                        setEventDetailOpen(true);
                        setIsOpen(false);
                        // Mark as read
                        if (!notif.read) {
                          api.put('/event-notifications/mark-read', { ids: [notif.id] }).catch(() => {});
                          setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                          setUnreadCount(prev => Math.max(0, prev - 1));
                        }
                      }}
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${severityColor}`} />
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            {isSpeed && <Gauge className="w-3 h-3 text-red-500 flex-shrink-0" />}
                            {notif.event_type === 'beacon_low_battery' && <BatteryLow className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                            {notif.event_type === 'device_disconnected' && <Power className="w-3 h-3 text-red-500 flex-shrink-0" />}
                            <span className="text-xs font-semibold truncate">{notif.event_rule_name || (notif.notification_type === 'driver_confirmation' ? 'Driver confirmation' : (notif.event_type === 'beacon_low_battery' ? 'Low Beacon Battery' : (notif.event_type === 'device_disconnected' ? 'Device Disconnected' : '')))}</span>
                            {!notif.read && <span className="ml-auto rounded-full bg-sky-700 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white dark:bg-cyan-300 dark:text-cyan-950" data-testid={`notification-unread-badge-${notif.id}`}>Unread</span>}
                          </div>
                          {notif.notification_type !== 'audit_alert' && (
                            <p className={`text-xs font-medium truncate ${!notif.read ? 'text-sky-950 dark:text-cyan-50' : 'text-slate-700 dark:text-slate-200'}`} data-testid={`notification-device-${notif.id}`}>{formatRecordDeviceLabel(notif)}</p>
                          )}
                          <p className={`text-[11px] truncate ${!notif.read ? 'text-sky-900 dark:text-cyan-100' : 'text-muted-foreground'}`}>{displayMessage}</p>
                          <p className={`text-[10px] ${!notif.read ? 'text-sky-800 dark:text-cyan-200' : 'text-muted-foreground'}`}>
                            {notifTriggeredAt ? formatDT(notifTriggeredAt, { timezone: notifTz, seconds: true }) : ''}
                            {' — '}{notifTzLabel}
                          </p>
                          {(() => {
                            const scanAction = scanAlertQuickAction(notif.event_type, notif.scan_credential)
                              || personOpenAction(notif.event_type, notif.person_type, notif.person_id);
                            if (!scanAction) return null;
                            return (
                              <button
                                type="button"
                                className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                data-testid={`notification-scan-quick-action-${notif.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(scanAction.url);
                                  setIsOpen(false);
                                  if (!notif.read) {
                                    api.put('/event-notifications/mark-read', { ids: [notif.id] }).catch(() => {});
                                    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                                    setUnreadCount(prev => Math.max(0, prev - 1));
                                  }
                                }}
                              >
                                {React.createElement(scanAction.kind === 'person' ? UserRound : CreditCard, { className: 'w-3 h-3 flex-shrink-0' })}
                                {scanAction.label}
                              </button>
                            );
                          })()}
                        </div>
                        {notif.event_type === 'video_event' && videoThumbs[notif.id]?.video_id && (
                          <div
                            className="relative w-16 h-11 rounded-md overflow-hidden flex-shrink-0 border border-border cursor-pointer group mt-0.5 bg-slate-800"
                            data-testid={`notification-video-thumb-${notif.id}`}
                            title="Open video clip"
                            onClick={(e) => { e.stopPropagation(); openExactClip(notif, videoThumbs[notif.id]); }}
                          >
                            {videoThumbs[notif.id].thumbnail_url && (
                              <img
                                src={videoThumbs[notif.id].thumbnail_url}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                              />
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/35 group-hover:bg-black/20 transition-colors">
                              <Play className="w-4 h-4 text-white fill-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal
        event={selectedEvent}
        open={eventDetailOpen}
        onOpenChange={setEventDetailOpen}
        onViewDevice={(deviceId, lat, lng, eventId) => navigate(`/tracking?select=${deviceId}${lat != null && lng != null ? `&lat=${lat}&lng=${lng}&zoom=17` : ''}${eventId ? `&event=${eventId}` : ''}`)}
      />

      {/* Phase 3B — Driver confirmation dialog (manager) */}
      {!isPersonLogin && (
        <DriverConfirmDialog
          api={api}
          candidateId={driverCandidateId}
          open={driverConfirmOpen}
          onOpenChange={setDriverConfirmOpen}
          onResolved={() => {
            if (driverCandidateId) toast.dismiss(`driver-conf-${driverCandidateId}`);
            fetchNotifications();
          }}
        />
      )}

      {/* Phase 3C — person accept/decline dialog */}
      {isPersonLogin && (
        <DriverRespondDialog
          api={api}
          candidateId={driverCandidateId}
          open={driverRespondOpen}
          onOpenChange={setDriverRespondOpen}
          personId={user?.person_ref_id}
          onResolved={fetchNotifications}
        />
      )}
    </div>
  );
};
